import { type Dispatch, type SetStateAction } from 'react'

import { USStates } from 'components/reusable/form/StateDropdown'
import { SplitFilterButton } from 'components/reusable/SplitFilterButton'
import { Button } from 'slab'
import { useGetAdminMedicalPlansYears } from 'utils/web_api'

import { getDefaultYear, type MedicalPlanFilters } from './hooks/useMedicalPlanFilters'

type MedicalPlansQuickFiltersProps = {
  filters: MedicalPlanFilters
  setFilters: Dispatch<SetStateAction<MedicalPlanFilters>>
}

const MedicalPlansQuickFilters = ({
  filters,
  setFilters,
}: MedicalPlansQuickFiltersProps) => {
  const { data: years } = useGetAdminMedicalPlansYears<string[]>()
  const defaultYear = getDefaultYear()

  // Get available years for dropdown
  const yearOptions = years || []
  const currentYear = filters.year || defaultYear

  // Get available states for dropdown
  const availableStates = USStates.filter((s) => s.value !== '')

  // Toggle off market only
  const toggleOffMarketOnly = () => {
    setFilters((prev) => ({
      ...prev,
      offMarketOnly: !prev.offMarketOnly,
    }))
  }

  // Toggle actively marketed only
  const toggleActivelyMarketedOnly = () => {
    setFilters((prev) => ({
      ...prev,
      activelyMarketedOnly: !prev.activelyMarketedOnly,
    }))
  }

  return (
    <div className='ml-1 flex items-center justify-start gap-2 flex-shrink-0 flex-wrap'>
      <div className='text-sm font-semibold min-w-16'>Quick filters</div>

      {/* Plan Year Filter */}
      <SplitFilterButton
        variant='primary'
        tooltip='Filter by plan year'
        primaryOption={{
          label: `Year: ${currentYear}`,
          handleSelect: () => {
            // If already set to current year, do nothing, otherwise set to current year
            if (currentYear !== defaultYear) {
              setFilters((prev) => ({ ...prev, year: defaultYear }))
            }
          },
        }}
        additionalOptions={yearOptions
          .filter((year) => year !== currentYear)
          .map((year) => ({
            label: year,
            handleSelect: () => setFilters((prev) => ({ ...prev, year })),
          }))}
      />

      {/* State Filter */}
      {filters.state
        ? (
          <SplitFilterButton
            variant='primary'
            tooltip='Filter by state'
            primaryOption={{
              label: `State: ${availableStates.find((s) => s.value === filters.state)?.label || filters.state}`,
              handleSelect: () => {
                // Clear state filter
                setFilters((prev) => ({
                  ...prev,
                  state: undefined,
                }))
              },
            }}
            additionalOptions={[
              {
                label: 'All',
                handleSelect: () => {
                  setFilters((prev) => ({
                    ...prev,
                    state: undefined,
                  }))
                },
              },
              ...availableStates
                .filter((s) => s.value !== filters.state)
                .map((state) => ({
                  label: state.label,
                  handleSelect: () => setFilters((prev) => ({ ...prev, state: state.value })),
                })),
            ]}
          />
          )
        : (
          <SplitFilterButton
            variant='tertiary'
            tooltip='Filter by state'
            primaryOption={{
              label: 'State: All',
              handleSelect: () => {
                // Do nothing, already showing all states
              },
            }}
            additionalOptions={availableStates.map((state) => ({
              label: state.label,
              handleSelect: () => setFilters((prev) => ({ ...prev, state: state.value })),
            }))}
          />
          )}

      {/* Off Market Only Toggle */}
      <Button
        variant={filters.offMarketOnly === true ? 'primary' : 'tertiary-deemphasized'}
        onClick={toggleOffMarketOnly}
      >
        Off Market Only
      </Button>

      {/* Actively Marketed Only Toggle */}
      <Button
        variant={filters.activelyMarketedOnly === true ? 'primary' : 'tertiary-deemphasized'}
        onClick={toggleActivelyMarketedOnly}
      >
        Actively Marketed Only
      </Button>
    </div>
  )
}

export default MedicalPlansQuickFilters
